import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import type { Project } from '../../types/admin';
import type {
  ApplicantInput,
  AvailableUnit,
  CalculatePlanInput,
  ConditionsInput,
  ConditionsResult,
  CreateSeparationInput,
  InstallmentInput,
  InstallmentsResult,
  PaymentPlanPreview,
  SeparationDraftResponse,
} from '../../types/separations';

export function useAvailableProjects(enabled = true) {
  return useQuery<Project[]>({
    queryKey: ['projects', 'available'],
    queryFn: () => apiClient.projects.available(),
    enabled,
  });
}

/**
 * Todas las unidades del proyecto con su estado (fix-50 · H-08): las separadas o vendidas se muestran
 * marcadas y no seleccionables (HU-C01 CA-2); `POST /separations` las sigue rechazando.
 */
export function useAvailableUnits(proyectoId: string) {
  return useQuery<AvailableUnit[]>({
    queryKey: ['separations', 'available-units', proyectoId, 'con-no-disponibles'],
    queryFn: () => apiClient.separations.availableUnits(proyectoId, { incluirNoDisponibles: true }),
    enabled: Boolean(proyectoId),
  });
}

export function useCreateSeparation() {
  return useMutation<SeparationDraftResponse, Error, CreateSeparationInput>({
    mutationFn: (input) => apiClient.separations.create(input),
  });
}

/** Paso 2: guarda los separantes y, después, registra la autorización de datos (HU-C07). */
export function useSaveApplicantsWithAuthorization() {
  return useMutation<void, Error, { id: string; separantes: ApplicantInput[] }>({
    mutationFn: async ({ id, separantes }) => {
      await apiClient.separations.setApplicants(id, separantes);
      await apiClient.separations.authorize(id);
    },
  });
}

export function useCalculatePlan() {
  return useMutation<PaymentPlanPreview, Error, CalculatePlanInput>({
    mutationFn: (input) => apiClient.paymentPlans.calculate(input),
  });
}

/** Paso 3 (fix-50): persiste las condiciones ANTES de las cuotas (calcular → PUT /condiciones → PUT /cuotas). */
export function useSaveConditions() {
  return useMutation<ConditionsResult, Error, { id: string; condiciones: ConditionsInput }>({
    mutationFn: ({ id, condiciones }) => apiClient.separations.setConditions(id, condiciones),
  });
}

export function useSaveInstallments() {
  return useMutation<InstallmentsResult, Error, { id: string; cuotas: InstallmentInput[] }>({
    mutationFn: ({ id, cuotas }) => apiClient.separations.setInstallments(id, cuotas),
  });
}
